import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { products } from '@/lib/data'
import { ProductCard } from '@/components/ProductCard'
import { gtmEvents } from '@/lib/gtm'
import { ArrowRight, ShieldCheck, Truck, Headphones } from 'lucide-react'

const Index = () => {
  // Ensure the new DP6 products are displayed (they are the first ones in the list)
  const featuredProducts = products.slice(0, 4)

  useEffect(() => {
    gtmEvents.viewItemList(
      featuredProducts.map((p) => ({
        item_id: p.id,
        item_name: p.name,
        price: p.price,
        item_category: p.category,
      })),
    )
  }, [])

  return (
    <div className="flex flex-col gap-12 pb-12">
      {/* Hero Section */}
      <section className="bg-white border-b relative overflow-hidden">
        <div className="container mx-auto px-4 py-16 md:py-24 flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 space-y-6 z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900">
              Vista sua paixão por <span className="text-blue-600">dados</span>.
            </h1>
            <p className="text-lg text-slate-600 max-w-lg">
              A única loja feita por engenheiros de dados, para engenheiros de
              dados.
            </p>
            <Button size="lg" className="h-14 px-8 text-lg" asChild>
              <Link to="/shop">
                Ver Coleção <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
          <div className="flex-1 relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-3xl -z-10" />
            <img
              src="https://img.usecurling.com/p/600/600?q=cartoon%20programmer%20coffee%20data&color=blue"
              alt="Developer Illustration"
              className="w-full max-w-md mx-auto hover:animate-float transition-transform"
            />
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-8 border-y bg-white/50 rounded-xl px-8">
          <div className="flex items-center gap-4 justify-center md:justify-start">
            <div className="p-3 bg-blue-100 rounded-full text-blue-600">
              <Truck className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold">Entrega Rápida</h3>
              <p className="text-sm text-muted-foreground">
                Para todo o Brasil
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 justify-center md:justify-start">
            <div className="p-3 bg-green-100 rounded-full text-green-600">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold">Checkout Seguro</h3>
              <p className="text-sm text-muted-foreground">Dados protegidos</p>
            </div>
          </div>
          <div className="flex items-center gap-4 justify-center md:justify-start">
            <div className="p-3 bg-purple-100 rounded-full text-purple-600">
              <Headphones className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold">Suporte Tech</h3>
              <p className="text-sm text-muted-foreground">
                Falamos sua língua
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="container mx-auto px-4">
        <h2 className="text-2xl font-bold mb-8">Categorias</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/shop?category=Camisetas"
            className="group relative h-64 overflow-hidden rounded-2xl bg-slate-900"
          >
            <img
              src="https://img.usecurling.com/p/800/400?q=t-shirts%20clothes%20rack&color=black"
              alt="Camisetas"
              className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 flex flex-col justify-end p-8">
              <h3 className="text-3xl font-bold text-white mb-2">Camisetas</h3>
              <span className="text-slate-200 flex items-center gap-2 group-hover:translate-x-2 transition-transform">
                Ver produtos <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
          <Link
            to="/shop?category=Canecas"
            className="group relative h-64 overflow-hidden rounded-2xl bg-slate-900"
          >
            <img
              src="https://img.usecurling.com/p/800/400?q=mugs%20coffee%20shelf&color=blue"
              alt="Canecas"
              className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 flex flex-col justify-end p-8">
              <h3 className="text-3xl font-bold text-white mb-2">Canecas</h3>
              <span className="text-slate-200 flex items-center gap-2 group-hover:translate-x-2 transition-transform">
                Ver produtos <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Featured Products */}
      <section className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold">Destaques</h2>
          <Link
            to="/shop"
            className="text-blue-600 hover:underline font-medium"
          >
            Ver tudo
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>
    </div>
  )
}

export default Index
